import React, { Component } from 'react'
import { Form, HelpBlock } from 'react-bootstrap'
import '../App.css'
import { embedComponent } from '../embed'
import Field from '../components/Field'
import CurrencyField from '../components/CurrencyField'
import formatCurrency from '../utils/formatters/formatCurrency'
import requiredPaymentToGoal from '../utils/calculations/requiredPaymentToGoal'
import Calculator from '../components/Calculator'
import PercentageField from '../components/PercentageField'
import CalculatorResult from '../components/CalculatorResult'
import CalculatorResults from '../components/CalculatorResults'
import CalculatorButtons from '../components/CalculatorButtons'
import { minMaxValidator } from '../utils/validators/index'
import formatPercentage from '../utils/formatters/formatPercentage'
import Annotations from '../components/Annotations'
import parseIntValue from '../utils/parsers/parseIntValue'
import { DEFAULT_MAX_DOLLAR_VALUE } from '../utils/validators'

const popoverMessages = {
  yearsUntilRetirement: {
    title: 'Years until retirement',
    body: [
      'You plan to retire in this number of years.',
    ],
  },
  inflationRate: {
    title: 'Inflation rate',
    body: [
      'The inflation rate plays an important role in future costs.',
      'For 2018-2022 the US inflation rate is projected to be between 2% and 3%.',
    ],
  },
  currentAnnualIncome: {
    title: 'Current annual income',
    body: [
      'This is your total annual income.\n',
      'Include salary, as well as other sources of regular income, but not intermittent income like eBay sales.',
    ],
  },
  incomeAtRetirement: {
    title: 'Income at retirement',
    body: [
      'This is calculated based on your current income, the inflation rate and the number of years until you retire.\n',
      'You can overwrite the calculated entry and put in your own number here if you wish.',
    ]
  },
  savingsNeeded: {
    title: 'Savings needed',
    body: [
      'This number is 12 times your income at retirement.\n',
      'Many experts believe this is the amount of money you will need to retire.\n',
      '<a href="/#/articles/468" target="_blank">Click here to learn more about retirement costs</a>.',
    ],
  },
  currentSavings: {
    title: 'Current savings',
    body: [
      'Enter your total current savings including your 401k and other savings plans.',
    ],
  },
  interestOnSavings: {
    title: 'Interest on savings',
    body: [
      'This is the annual interest rate you get on your savings.',
    ],
  },
  monthlySavingsAmount: {
    title: 'You need to save this amount/month',
    body: [
      'This is the amount you need to set aside each month for the number of years you indicated to save the amount you need.',
    ],
  },
}

class RetirementNestEggCalculator extends Component {
  constructor (props) {
    super(props)

    this.state = RetirementNestEggCalculator.getInitialState()

    this.clearState = this.clearState.bind(this)
    this.onValidation = this.onValidation.bind(this)
  }

  static getInitialState() {
    return {
      yearsUntilRetirement: null,
      inflationRate: 2.5,
      currentAnnualIncome: null,
      incomeAtRetirement: null,
      savingsNeeded: null,
      currentSavings: null,
      interestOnSavings: null,
      fieldErrors: {},
    }
  }

  handleChange (value, fieldName) {
    this.setState({ [fieldName]: value })
  }

  clearState () {
    this.setState(RetirementNestEggCalculator.getInitialState())
  }

  onValidation(name, error) {
    if (typeof error !== 'undefined') {
      // Has errors
      if (!this.state.fieldErrors[name]) {
        this.setState({ fieldErrors: Object.assign({}, this.state.fieldErrors, { [name]: error }) })
      }
    } else {
      // No errors
      if (this.state.fieldErrors[name]) {
        const newErrors = Object.assign({}, this.state.fieldErrors)
        delete newErrors[name]
        this.setState({ fieldErrors: newErrors })
      }
    }
  }

  calculateMonthlySavingsAmount () {
    const { currentSavings, interestOnSavings, yearsUntilRetirement, savingsNeeded } = this.state

    const rawSavingsGoal = savingsNeeded || this.calculateSavingsNeeded()

    if (!rawSavingsGoal || !interestOnSavings) {
      return undefined
    }

    return requiredPaymentToGoal(rawSavingsGoal, interestOnSavings / 100, yearsUntilRetirement, currentSavings || 0)
  }

  /**
   * Formula: = 12 * Income at retirement.
   */
  calculateSavingsNeeded () {
    const { incomeAtRetirement } = this.state

    const income = incomeAtRetirement || this.calculateIncomeAtRetirement()

    if (!income) {
      return incomeAtRetirement
    }

    return 12 * income;
  }

  /**
   * IAR = CurrInc. * (1 + r)^n
   */
  calculateIncomeAtRetirement () {
    const { currentAnnualIncome, inflationRate, yearsUntilRetirement, incomeAtRetirement } = this.state

    if (!currentAnnualIncome || !inflationRate || !yearsUntilRetirement) {
      return incomeAtRetirement
    }

    return currentAnnualIncome * Math.pow(1 + (inflationRate / 100), yearsUntilRetirement)
  }

  render () {
    const {
      yearsUntilRetirement,
      inflationRate,
      currentAnnualIncome,
      incomeAtRetirement,
      savingsNeeded,
      currentSavings,
      interestOnSavings,
    } = this.state

    const calculatedMonthlySavingsAmount = this.calculateMonthlySavingsAmount();
    const calculatedSavingsNeeded = this.calculateSavingsNeeded();
    const calculatedIncomeAtRetirement = this.calculateIncomeAtRetirement();
    const hasErrors = Object.keys(this.state.fieldErrors).length > 0

    return (
      <Calculator title="Retirement Nest Egg Calculator">
        <div className="calculator-body">
          <Form>
            <Field
              type="number"
              name="yearsUntilRetirement"
              label="Years Until Retirement"
              value={yearsUntilRetirement}
              parse={parseIntValue}
              onChange={(e, value) => this.handleChange(value, 'yearsUntilRetirement') }
              popover={popoverMessages.yearsUntilRetirement}
              validator={minMaxValidator(0, 99)}
              onValidation={this.onValidation}
            />

            <PercentageField
              name="inflationRate"
              label="Inflation Rate"
              value={inflationRate}
              onChange={(e, value) => this.handleChange(value, 'inflationRate') }
              popover={popoverMessages.inflationRate}
              validator={minMaxValidator(0, 99, formatPercentage)}
              onValidation={this.onValidation}
            />

            <CurrencyField
              name="currentAnnualIncome"
              label="Current Annual Income"
              value={currentAnnualIncome}
              onChange={(e, value) => this.handleChange(value, 'currentAnnualIncome') }
              popover={popoverMessages.currentAnnualIncome}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <CurrencyField
              name="incomeAtRetirement"
              label="Income at Retirement"
              annotationLabel="*"
              value={incomeAtRetirement}
              calculatedValue={calculatedIncomeAtRetirement}
              onChange={(e, value) => this.handleChange(value, 'incomeAtRetirement') }
              popover={popoverMessages.incomeAtRetirement}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <CurrencyField
              name="savingsNeeded"
              label="Savings Needed"
              annotationLabel="**"
              value={savingsNeeded}
              calculatedValue={calculatedSavingsNeeded}
              onChange={(e, value) => this.handleChange(value, 'savingsNeeded') }
              popover={popoverMessages.savingsNeeded}
              readOnly
            />

            <CurrencyField
              name="currentSavings"
              label="Current Savings"
              value={currentSavings}
              onChange={(e, value) => this.handleChange(value, 'currentSavings') }
              popover={popoverMessages.currentSavings}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <PercentageField
              name="interestOnSavings"
              label="Interest Rate on Savings"
              value={interestOnSavings}
              onChange={(e, value) => this.handleChange(value, 'interestOnSavings') }
              popover={popoverMessages.interestOnSavings}
              validator={minMaxValidator(0, 99, formatPercentage)}
              onValidation={this.onValidation}
            />
          </Form>
          <CalculatorResults>
            <CalculatorResult
              label={'You need to save this amount/month'}
              value={formatCurrency(Math.ceil((!hasErrors && calculatedMonthlySavingsAmount) || 0))}
              popover={popoverMessages.monthlySavingsAmount}
              helpText={calculatedMonthlySavingsAmount === null && <HelpBlock>At the given interest rate you don't need to save any additional money per month.</HelpBlock>}
            />
          </CalculatorResults>
          <CalculatorButtons onClear={this.clearState} />
          <Annotations>
            <HelpBlock><sup>*</sup> Based on the inflation rate indicated.</HelpBlock>
            <HelpBlock><sup>**</sup> Experts estimate you need 12x your final income for retirement.</HelpBlock>
          </Annotations>
        </div>
      </Calculator>
    )
  }
}

embedComponent(RetirementNestEggCalculator, 'tgm-retirement-nest-egg-calc', 'tgm-retirement-nest-egg-calc-embed')

export default RetirementNestEggCalculator



// WEBPACK FOOTER //
// ./src/calculators/RetirementNestEggCalculator.js